import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrencyAR } from '@/utils/currencyFormatter';
import { getImageUrl } from '@/utils/imagePaths';
import { PhotoIcon, MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon, EyeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import { getAppSocket } from '@/lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '@/components/ui/tip';
import AppointmentCombobox, { AppointmentOption } from '@/components/AppointmentCombobox';
import { hoyLocal } from '../utils/fechaLocal';

type Supply = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  stock: number;
  minStock: number;
  location?: string;
  costPrice: number;
  supplier?: string;
  attachmentUrl?: string;
  isActive: number;
};

type SupplyMovement = {
  id: string;
  type: 'in' | 'out' | 'adjust';
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  reason?: string;
  notes?: string;
  performedBy?: string;
  serviceName?: string;
  createdAt: string;
};

type SupplyOverview = {
  supply: Supply;
  movements: SupplyMovement[];
  stats: {
    avgDaily: number;
    avgWeekly: number;
    projectedDays: number | null;
    lastOutAt?: string | null;
    abnormalConsumption: boolean;
    critical: boolean;
    byService: { serviceName: string; totalQty: number; totalCost: number }[];
  };
};

const SuppliesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [search, setSearch] = useState('');
  // ?low=1 permite deep-linkear la lista ya filtrada (alertas del tablero)
  const [lowStockOnly, setLowStockOnly] = useState(() => new URLSearchParams(location.search).get('low') === '1');
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [noConsumptionDays, setNoConsumptionDays] = useState('');
  const [highConsumption, setHighConsumption] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Registrar salida / consumo rápido de un insumo (consumo interno, cortesía,
  // rotura/merma, ajuste, etc.). Descuenta stock y queda en el historial.
  const [movSupply, setMovSupply] = useState<Supply | null>(null);
  const [movQty, setMovQty] = useState('1');
  const [movReason, setMovReason] = useState('Consumo interno');
  const [movReasonOther, setMovReasonOther] = useState('');
  const [movLinkType, setMovLinkType] = useState<'ot' | 'remito'>('ot');
  const [movOt, setMovOt] = useState<AppointmentOption | null>(null);
  const [movDate, setMovDate] = useState<string>(() => hoyLocal());
  const [movRecipient, setMovRecipient] = useState('');
  // Consumo interno: "a quién" es multi-select (puede consumir más de una persona)
  const [movRecipients, setMovRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  const [otList, setOtList] = useState<AppointmentOption[]>([]);
  const [remitoList, setRemitoList] = useState<AppointmentOption[]>([]);
  const [movSaving, setMovSaving] = useState(false);
  const [overview, setOverview] = useState<SupplyOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [showOverviewModal, setShowOverviewModal] = useState(false);

  const canManage = hasPermission('supplies');

  const fetchSupplies = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/supplies', {
        params: {
          page: 1,
          limit: 100,
          search,
          lowStock: lowStockOnly,
          outOfStock: outOfStockOnly,
          critical: criticalOnly,
          category: categoryFilter || undefined,
          supplier: supplierFilter || undefined,
          noConsumptionDays: noConsumptionDays || undefined,
          highConsumption
        }
      });
      setSupplies(res.data.supplies || []);
    } catch (error: any) {
      console.error('Error fetching supplies', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar los insumos');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, lowStockOnly, outOfStockOnly, criticalOnly, categoryFilter, supplierFilter, noConsumptionDays, highConsumption]);

  useEffect(() => {
    fetchSupplies();
  }, [fetchSupplies]);

  // Sugerencias de "a quién" (equipo). Best-effort: si no hay permiso, queda vacío
  // y el campo sigue funcionando como texto libre.
  useEffect(() => {
    axios.get('/api/staff-payments/employees')
      .then((r) => {
        const names = (r.data?.employees || [])
          .map((e: any) => String(e.name || `${e.firstName || ''} ${e.lastName || ''}`).trim())
          .filter(Boolean);
        setTeamMembers(Array.from(new Set(names)));
      })
      .catch(() => setTeamMembers([]));
  }, []);

  // Realtime: refrescar en vivo por sucursal (sin polling)
  const refreshTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/supplies') || path.startsWith('/api/purchases') || path.startsWith('/api/service-reports'))) {
        return;
      }

      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => {
        fetchSupplies({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchSupplies]);

  // Sembrar la búsqueda desde ?q= SOLO al navegar (cuando cambia la URL), no en
  // cada tecla: si dependiera de `search`, al borrar el texto el efecto volvería
  // a re-aplicar el q de la URL y no se podría borrar.
  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query) setSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Atajo de teclado para nuevo insumo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable) return;
      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/supplies/new');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoriesRes, providersRes] = await Promise.all([
          axios.get('/api/settings/areas'),
          axios.get('/api/providers', { params: { page: 1, limit: 200, isActive: true } })
        ]);
        const categories = (categoriesRes.data?.areas || []).map((c: any) => c.name).filter(Boolean);
        const suppliers = (providersRes.data?.providers || []).map((p: any) => p.name).filter(Boolean);
        setCategoryOptions(categories);
        setSupplierOptions(suppliers);
      } catch (error) {
        console.error('Error loading supply filters', error);
      }
    };
    fetchOptions();
  }, []);

  const handlePreviewImage = (attachmentUrl?: string | null) => {
    if (!attachmentUrl) return;
    const url = getImageUrl(attachmentUrl);
    if (url) {
      setPreviewImageUrl(url);
    }
  };

  const handleDelete = async (supply: Supply) => {
    const confirmed = await confirmDialog(`Eliminar el insumo "${supply.name}"?`);
    if (!confirmed) return;
    try {
      setDeletingId(supply.id);
      await axios.delete(`/api/supplies/${supply.id}`);
      toast.success('Insumo eliminado');
      fetchSupplies();
    } catch (error: any) {
      console.error('Error deleting supply', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar el insumo');
    } finally {
      setDeletingId(null);
    }
  };

  const openMovement = (supply: Supply) => {
    setMovSupply(supply);
    setMovQty('1');
    setMovReason('Consumo interno');
    setMovReasonOther('');
    setMovOt(null);
    setMovLinkType('ot');
    setMovDate(hoyLocal());
    setMovRecipient('');
    setMovRecipients([]);
    setRecipientInput('');
    // Cargar OTs y remitos para el enlace opcional (una sola vez)
    if (otList.length === 0) {
      axios.get('/api/appointments').then((r) => {
        const list: any[] = r.data.appointments || r.data || [];
        const otNum = (a: any) => { const m = String(a.appointmentCode || '').match(/(\d+)/); return m ? parseInt(m[1], 10) : -1; };
        const ts = (a: any) => (a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0);
        const relevant = list
          .filter((a) => a.status !== 'cancelled')
          .sort((a, b) => (otNum(b) - otNum(a)) || (ts(b) - ts(a)))
          .slice(0, 100);
        setOtList(relevant);
      }).catch(() => {});
    }
    if (remitoList.length === 0) {
      axios.get('/api/remitos?limit=200').then((r) => {
        const list: any[] = r.data.remitos || r.data || [];
        // Mapeo al formato del combobox: código = N° de remito
        const mapped: AppointmentOption[] = list.map((rm: any) => ({
          id: rm.id,
          appointmentCode: rm.remitNumber,
          scheduledDate: rm.createdAt || rm.issuedAt,
          clientName: rm.clientName,
          vehiclePlate: rm.licensePlate,
          serviceType: 'Remito',
        }));
        setRemitoList(mapped);
      }).catch(() => {});
    }
  };

  const submitMovement = async () => {
    if (!movSupply) return;
    const qty = Number(movQty);
    if (!qty || qty <= 0) { toast.error('Ingresá una cantidad válida'); return; }
    const reason = (movReason === 'Otro' ? movReasonOther.trim() : movReason) || 'Consumo';
    const serviceName = movOt
      ? [movOt.appointmentCode, movLinkType === 'remito' ? 'Remito' : movOt.serviceType].filter(Boolean).join(' · ')
      : null;
    try {
      setMovSaving(true);
      await axios.post(`/api/supplies/${movSupply.id}/movements`, {
        type: 'out',
        quantity: qty,
        reason,
        appointmentId: movLinkType === 'ot' ? (movOt?.id || null) : null,
        remitoId: movLinkType === 'remito' ? (movOt?.id || null) : null,
        serviceName,
        // Consumo interno: varios destinatarios (multi). Otros motivos: texto simple.
        recipient: (movReason === 'Consumo interno'
          ? movRecipients.join(', ')
          : movRecipient.trim()) || null,
        date: movDate || undefined,
      });
      toast.success(`Salida registrada: ${qty} de "${movSupply.name}"`);
      setMovSupply(null);
      fetchSupplies();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo registrar la salida');
    } finally {
      setMovSaving(false);
    }
  };

  const openOverview = async (supply: Supply) => {
    setShowOverviewModal(true);
    setOverview(null);
    try {
      setOverviewLoading(true);
      const res = await axios.get(`/api/supplies/${supply.id}/overview`);
      setOverview(res.data || null);
    } catch (error: any) {
      console.error('Error loading supply overview', error);
      toast.error(error.response?.data?.error || 'No se pudo cargar el detalle del insumo');
    } finally {
      setOverviewLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background relative">
        <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Insumos</h1>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => navigate('/supplies/stock')}
                className="inline-flex items-center gap-1.5 h-9 rounded-lg"
              >
                Stock
              </Button>
              <Button
                onClick={() => navigate('/supplies/new')}
                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Nuevo insumo
                <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
                  N
                </kbd>
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-80">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
            <Input
              placeholder="Buscar por nombre o categoría..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-w-0 rounded-lg border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-9 pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <Switch
                className="data-[state=checked]:bg-emerald-500"
                id="lowStock"
                checked={lowStockOnly}
                onCheckedChange={setLowStockOnly}
              />
              <span>Stock bajo</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                className="data-[state=checked]:bg-emerald-500"
                id="outStock"
                checked={outOfStockOnly}
                onCheckedChange={setOutOfStockOnly}
              />
              <span>Sin stock</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                className="data-[state=checked]:bg-emerald-500"
                id="critical"
                checked={criticalOnly}
                onCheckedChange={setCriticalOnly}
              />
              <span>Críticos</span>
            </label>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label>Categoría</Label>
            <Select value={categoryFilter || 'all'} onValueChange={(value) => setCategoryFilter(value === 'all' ? '' : value)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categoryOptions.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Proveedor</Label>
            <Select value={supplierFilter || 'all'} onValueChange={(value) => setSupplierFilter(value === 'all' ? '' : value)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {supplierOptions.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Sin consumo (días)</Label>
            <Input
              type="number"
              min={0}
              placeholder="Ej: 30"
              value={noConsumptionDays}
              onChange={(e) => setNoConsumptionDays(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                className="data-[state=checked]:bg-emerald-500"
                id="highConsumption"
                checked={highConsumption}
                onCheckedChange={setHighConsumption}
              />
              <span>Alto consumo</span>
            </label>
          </div>
        </div>

        <Card className="mt-6">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground"></div>
                <p className="text-sm text-muted-foreground">Cargando insumos...</p>
              </div>
            ) : !supplies.length ? (
              <div className="text-center py-12">
                <PhotoIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay insumos cargados</p>
              </div>
            ) : (
              <>
                <div className="md:hidden space-y-3 p-4">
                  {supplies.map((s) => {
                    const low = s.stock <= s.minStock;
                    const out = s.stock === 0;
                    return (
                      <div key={s.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Insumo</p>
                            <p className="text-base font-semibold text-foreground">{s.name}</p>
                            {s.description && (
                              <p className="text-sm text-muted-foreground">{s.description}</p>
                            )}
                          </div>
                          {low && (
                            <Badge variant="secondary" className={out ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}>
                              {out ? 'Sin stock' : 'Bajo'}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Stock</p>
                            <p className="font-medium text-foreground">{s.stock}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Minimo</p>
                            <p className="font-medium text-foreground">{s.minStock}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Unidad</p>
                            <p className="font-medium text-foreground capitalize">{s.unit}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Costo prom.</p>
                            <p className="font-medium text-foreground">{formatCurrencyAR(s.costPrice || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Categoria</p>
                            <p className="font-medium text-foreground">{s.category || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Proveedor</p>
                            <p className="font-medium text-foreground">{s.supplier || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase text-muted-foreground">Ubicacion</p>
                            <p className="font-medium text-foreground">{s.location || '-'}</p>
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[110px] justify-center"
                              onClick={() => navigate(`/supplies/${s.id}/view`)}
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[110px] justify-center"
                              onClick={() => navigate(`/supplies/${s.id}/edit`, { state: { supply: s } })}
                            >
                              <PencilSquareIcon className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[110px] justify-center text-destructive"
                              disabled={deletingId === s.id}
                              onClick={() => handleDelete(s)}
                            >
                              <TrashIcon className="h-4 w-4 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="cq-table relative w-full hidden md:block">
                  <Table className="w-full">
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Stock</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-narrow">Minimo</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-narrow">Unidad</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-narrow">Categoría</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Proveedor</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-narrow">Ubicación</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right">Costo promedio</TableHead>
                      {canManage && (
                        <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right">
                          Acciones
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplies.map((s) => {
                      const low = s.stock <= s.minStock;
                      const out = s.stock === 0;
                      return (
                        <TableRow
                          key={s.id}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/supplies/${s.id}/view`)}
                        >
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap max-w-[220px]">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePreviewImage(s.attachmentUrl);
                              }}
                              className="font-medium text-left hover:underline block w-full truncate"
                            >
                              {s.name}
                            </button>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span>{s.stock}</span>
                              {low && (
                                <Badge variant="secondary" className={out ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}>
                                  {out ? 'Sin stock' : 'Bajo'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap cq-hide-narrow">{s.minStock}</TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap capitalize cq-hide-narrow">{s.unit}</TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap cq-hide-narrow">{s.category || '-'}</TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap">{s.supplier || '-'}</TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap cq-hide-narrow">{s.location || '-'}</TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right">{formatCurrencyAR(s.costPrice || 0)}</TableCell>
                          {canManage && (
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end">
                                <ButtonGroup>
                                  <Tip label="Ver detalle">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="size-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/supplies/${s.id}/view`);
                                      }}
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  <Tip label="Editar insumo">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="size-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/supplies/${s.id}/edit`, { state: { supply: s } });
                                      }}
                                    >
                                      <PencilSquareIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  <Tip label="Registrar salida / consumo">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="size-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openMovement(s);
                                      }}
                                    >
                                      <ArrowUpTrayIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  <Tip label="Eliminar insumo">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="size-8"
                                      disabled={deletingId === s.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(s);
                                      }}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                </ButtonGroup>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Registrar salida / consumo de un insumo */}
      <Dialog open={Boolean(movSupply)} onOpenChange={(open) => !open && setMovSupply(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar salida de insumo</DialogTitle>
          </DialogHeader>
          {movSupply && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-medium">{movSupply.name}</span>
                <span className="block text-xs text-muted-foreground">Stock actual: {movSupply.stock} {movSupply.unit || ''}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Cantidad *</Label>
                  <Input type="number" min="1" value={movQty} onChange={(e) => setMovQty(e.target.value)} className="h-9" />
                </div>
                <div className="space-y-1.5">
                  <Label>Motivo</Label>
                  <Select value={movReason} onValueChange={(v) => { setMovReason(v); if (v === 'Consumo interno') setMovOt(null); }}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Consumo interno">Consumo interno</SelectItem>
                      <SelectItem value="Cortesía a cliente">Cortesía a cliente</SelectItem>
                      <SelectItem value="Rotura / merma">Rotura / merma</SelectItem>
                      <SelectItem value="Ajuste de stock">Ajuste de stock</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {movReason === 'Otro' && (
                <div className="space-y-1.5">
                  <Label>Detalle del motivo</Label>
                  <Input value={movReasonOther} onChange={(e) => setMovReasonOther(e.target.value)} placeholder="Ej: muestra, regalo..." className="h-9" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Fecha</Label>
                  <DatePicker value={movDate} onChange={setMovDate} className="h-9 w-full" />
                </div>
                <div className="space-y-1.5">
                  <Label>A quién / Para {movReason === 'Consumo interno' ? '(uno o varios)' : '(opcional)'}</Label>
                  {movReason === 'Consumo interno' ? (
                    <div className="space-y-1.5">
                      <Input
                        list="team-members"
                        value={recipientInput}
                        onChange={(e) => setRecipientInput(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ',') && recipientInput.trim()) {
                            e.preventDefault();
                            const v = recipientInput.trim();
                            setMovRecipients((prev) => prev.includes(v) ? prev : [...prev, v]);
                            setRecipientInput('');
                          }
                        }}
                        onBlur={() => {
                          const v = recipientInput.trim();
                          if (v) { setMovRecipients((prev) => prev.includes(v) ? prev : [...prev, v]); setRecipientInput(''); }
                        }}
                        placeholder="Escribí un nombre y Enter..."
                        className="h-9"
                      />
                      <datalist id="team-members">
                        {teamMembers.filter((n) => !movRecipients.includes(n)).map((n) => <option key={n} value={n} />)}
                      </datalist>
                      {movRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {movRecipients.map((n) => (
                            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs">
                              {n}
                              <button type="button" onClick={() => setMovRecipients((prev) => prev.filter((x) => x !== n))} className="text-muted-foreground hover:text-foreground">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Input value={movRecipient} onChange={(e) => setMovRecipient(e.target.value)} placeholder="Ej: Juan, oficina, cliente..." className="h-9" />
                  )}
                </div>
              </div>
              {/* El consumo interno no se ata a ninguna OT/remito */}
              {movReason !== 'Consumo interno' && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Documento relacionado (opcional)</Label>
                  <div className="flex rounded-md border border-border overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => { setMovLinkType('ot'); setMovOt(null); }}
                      className={`px-2.5 py-1 ${movLinkType === 'ot' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                    >
                      OT
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMovLinkType('remito'); setMovOt(null); }}
                      className={`px-2.5 py-1 border-l border-border ${movLinkType === 'remito' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                    >
                      Remito
                    </button>
                  </div>
                </div>
                <AppointmentCombobox
                  key={movLinkType}
                  appointments={movLinkType === 'ot' ? otList : remitoList}
                  selected={movOt}
                  onSelect={setMovOt}
                  placeholder={movLinkType === 'ot' ? 'Buscar OT por código, patente o cliente...' : 'Buscar remito por número, patente o cliente...'}
                />
                <p className="text-xs text-muted-foreground">Enlazala a una OT o remito para seguir el consumo por trabajo (opcional).</p>
              </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setMovSupply(null)}>Cancelar</Button>
                <Button
                  onClick={submitMovement}
                  disabled={movSaving}
                  className="bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
                >
                  {movSaving ? 'Registrando...' : 'Registrar salida'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewImageUrl)} onOpenChange={(open) => !open && setPreviewImageUrl(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Foto del insumo</DialogTitle>
          </DialogHeader>
          {previewImageUrl && (
            <div className="flex items-center justify-center">
              <img
                src={previewImageUrl}
                alt="Foto del insumo"
                className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showOverviewModal} onOpenChange={setShowOverviewModal}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Detalle del insumo</DialogTitle>
          </DialogHeader>
          {overviewLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : overview ? (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-4">
                <Card>
                  <CardContent className="p-4 space-y-1">
                    <p className="text-xs text-muted-foreground">Consumo diario</p>
                    <p className="text-2xl font-semibold">{overview.stats.avgDaily.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 space-y-1">
                    <p className="text-xs text-muted-foreground">Consumo semanal</p>
                    <p className="text-2xl font-semibold">{overview.stats.avgWeekly.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 space-y-1">
                    <p className="text-xs text-muted-foreground">Stock proyectado</p>
                    <p className="text-2xl font-semibold">
                      {overview.stats.projectedDays === null ? 'n/a' : `${overview.stats.projectedDays} d`}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <p className="text-xs text-muted-foreground">Alertas inteligentes</p>
                    <div className="flex flex-wrap gap-2">
                      {overview.stats.critical && <Badge className="bg-red-100 text-red-700">Insumo crítico</Badge>}
                      {overview.stats.abnormalConsumption && <Badge className="bg-amber-100 text-amber-800">Consumo anormal</Badge>}
                      {overview.stats.projectedDays !== null && overview.stats.projectedDays <= 7 && (
                        <Badge className="bg-rose-100 text-rose-700">Stock proyectado &lt; 7 días</Badge>
                      )}
                      {!overview.stats.critical && !overview.stats.abnormalConsumption && (
                        <Badge className="bg-emerald-100 text-emerald-700">Sin alertas</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold mb-3">Consumo por servicio (60 días)</h3>
                    {overview.stats.byService?.length ? (
                      <div className="space-y-2 text-sm">
                        {overview.stats.byService.map((row) => (
                          <div key={row.serviceName} className="flex items-center justify-between gap-3">
                            <div className="text-muted-foreground">{row.serviceName}</div>
                            <div className="text-right font-medium">
                              {row.totalQty.toFixed(2)} | {formatCurrencyAR(row.totalCost || 0)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin datos de servicios asociados.</p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold mb-3">Información general</h3>
                    <div className="text-sm text-muted-foreground space-y-2">
                      <div><span className="font-medium text-foreground">Insumo:</span> {overview.supply.name}</div>
                      <div><span className="font-medium text-foreground">Categoría:</span> {overview.supply.category || '-'}</div>
                      <div><span className="font-medium text-foreground">Proveedor:</span> {overview.supply.supplier || '-'}</div>
                      <div><span className="font-medium text-foreground">Costo promedio:</span> {formatCurrencyAR(overview.supply.costPrice || 0)}</div>
                      <div><span className="font-medium text-foreground">Último consumo:</span> {overview.stats.lastOutAt ? new Date(overview.stats.lastOutAt).toLocaleDateString('es-AR') : 'n/a'}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b">
                    <h3 className="text-sm font-semibold">Historial de movimientos</h3>
                  </div>
                  <div className="max-h-[360px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Cantidad</TableHead>
                          <TableHead>Servicio</TableHead>
                          <TableHead>A quién</TableHead>
                          <TableHead>Usuario</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Motivo</TableHead>
                          <TableHead className="text-right">Costo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overview.movements.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell className="capitalize">{m.type === 'in' ? 'Entrada' : m.type === 'out' ? 'Salida' : 'Ajuste'}</TableCell>
                            <TableCell>{m.quantity}</TableCell>
                            <TableCell>{m.serviceName || '-'}</TableCell>
                            <TableCell>{(m as any).recipient || '-'}</TableCell>
                            <TableCell>{m.performedBy || '-'}</TableCell>
                            <TableCell>{new Date(m.createdAt).toLocaleDateString('es-AR')}</TableCell>
                            <TableCell>{m.reason || '-'}</TableCell>
                            <TableCell className="text-right">{formatCurrencyAR(m.totalCost || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center text-muted-foreground">No hay detalle para mostrar.</div>
          )}
        </DialogContent>
      </Dialog>

      </div>
    </ErrorBoundary>
  );
};

export default SuppliesPage;
